import { ReactNode, useEffect, useState, useRef } from "react";
import SplashPage from "../components/pages/SplashPage";
import { ProtectContext } from "../context/protect_context";
import configService from "../services/service/config_service";
import type { AppData, Footer } from "../types";
import { REACT_APP_API_BASE_URL } from "../services/api";

export const ProtectProvider = ({ children }: { children: ReactNode }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initProgress, setInitProgress] = useState(0);
  const [data, setData] = useState<AppData | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isDataLoadedRef = useRef(false);

  useEffect(() => {
    // Start with initial progress
    setInitProgress(15);

    // Simulate realistic progress during fetch
    progressIntervalRef.current = setInterval(() => {
      setInitProgress((prev) => {
        // If data is already loaded, complete to 100%
        if (isDataLoadedRef.current) {
          return 100;
        }
        // Gradually increase progress, but cap at 85% until data loads
        const increment = Math.random() * 8 + 2; // Random increment between 2-10
        const newProgress = Math.min(prev + increment, 85);
        return newProgress;
      });
    }, 150); // Update every 150ms for smooth animation

    fetchData();

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const fetchData = async () => {
    try {
      // Fetch pages, settings, FAQs, and Advices in parallel
      const [pagesRes, settingsRes, faqsRes, advicesRes] = await Promise.all([
        configService.getConfigs(),
        fetch(`${REACT_APP_API_BASE_URL}/settings/public`).then(res => res.json()).catch(() => null),
        fetch(`${REACT_APP_API_BASE_URL}/faqs/public`).then(res => res.json()).catch(() => null),
        fetch(`${REACT_APP_API_BASE_URL}/advices/public`).then(res => res.json()).catch(() => null)
      ]);
      
      // pagesRes is ResponseModel, but get-values returns array directly
      // Check if it's already an array, or if it's wrapped in ResponseModel
      const pagesArray = Array.isArray(pagesRes) 
        ? pagesRes 
        : (Array.isArray((pagesRes as any)?.data) 
          ? (pagesRes as any).data 
          : (Array.isArray((pagesRes as any)?.datas) ? (pagesRes as any).datas : []));
      
      // Prepare footer data from settings
      let footerData: Footer | null = null;
      if (settingsRes?.success && settingsRes?.data) {
        const settings = settingsRes.data;
        footerData = {
          address: settings.footer?.address || "",
          phone: settings.footer?.phone || "",
          email: settings.footer?.email || "",
          copyright: settings.footer?.copyright || "",
          by: settings.footer?.by || "",
          data: settings.footer?.data || null,
        };
      }
      
      // Prepare config with logo
      const config = {
        logo: settingsRes?.data?.logo || null,
        url: "",
        protocol: "",
        isVerified: false,
        pageTitle: "",
        primaryColor: "",
        dataExported: "",
      };
      
      // Prepare FAQs
      const faqs = faqsRes?.success && Array.isArray(faqsRes?.data) ? faqsRes.data : [];
      
      // Prepare Advices
      const advices = advicesRes?.success && Array.isArray(advicesRes?.data) ? advicesRes.data : [];
      
      if (pagesArray.length > 0 || advices.length > 0 || faqs.length > 0) {
        isDataLoadedRef.current = true;
        setInitProgress(100);
        setTimeout(() => {
          setIsInitialized(true);
          setData({ 
            datas: pagesArray,
            footer: footerData,
            config: config,
            faqs: faqs,
            advices: advices,
          } as AppData);
        }, 300);
      }
    } catch (error) {
      console.error("ProtectProvider.fetchData():", error);
      // On error, still complete the progress
      isDataLoadedRef.current = true;
      setInitProgress(100);
      setTimeout(() => {
        setIsInitialized(true);
      }, 300);
    }
  };

  if (!isInitialized) {
    return <SplashPage progress={initProgress} />;
  }

  return (
    <ProtectContext.Provider
      value={{
        data: data,
      }}
    >
      {children}
    </ProtectContext.Provider>
  );
};
